// @flow
import React from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import { t } from 'Src/i18n';

import { type ImageSlideShowProps } from 'Components/frameworks/image-slideshow/ImageSlideShow.jsx';
import {
  LabeledIconsList,
  type LabeledIcon,
} from 'Components/frameworks/labeled-icons-list/LabeledIconsList.jsx';
import { Sheet } from 'Components/frameworks/sheet/Sheet.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

import { type BadgeProps } from 'Components/frameworks/badge/Badge.jsx';

type Props = {
  description?: string,
  diamond: BadgeProps,
  immersive: *,
  isExclusiveCollection?: boolean,
  overview: Array<LabeledIcon>,
  services: Array<{
    label: string,
    items: Array<LabeledIcon>,
  }>,
  slideshow?: ImageSlideShowProps,
  title: string,
};

export function ProductRoomDetail(props: Props) {
  const {
    description,
    diamond = {},
    immersive,
    isExclusiveCollection,
    overview,
    services = [],
    slideshow = {},
    title,
  } = props;

  const diamondProps = isExclusiveCollection
    ? {
        ...diamond,
        color: COLORS.DEEP_BLUE,
        colorBg: COLORS.WHITE,
        colorBorder: COLORS.DEEP_BLUE,
      }
    : {
        ...diamond,
        color: COLORS.WHITE,
        colorBg: diamond.color,
        colorBorder: diamond.color,
      };

  const slideshowProps = {
    ...slideshow,
    groupKey: 'roomDetail',
    hasInsetBorder: isExclusiveCollection,
    media: slideshow.media,
    nextLabel: t('common.next'),
    previousLabel: t('common.previous'),
  };

  const color = isExclusiveCollection ? COLORS.DEEP_BLUE : COLORS.BLUE;
  const textColor = isExclusiveCollection ? COLORS.DEEP_BLUE : COLORS.GRAY_DARKER;

  return (
    <Sheet diamond={diamondProps} immersive={immersive} slideshow={slideshowProps}>
      {!isEmpty(overview) && (
        <section className="border-b-1 pb-5">
          <div className="mx-auto" style={{ maxWidth: '33rem' }}>
            <LabeledIconsList items={overview} color={color} />
          </div>
        </section>
      )}
      <section className={classnames('py-5', { 'border-b-1': services.length })}>
        <Title
          className="sm:text-5xl"
          color={textColor}
          theme={isExclusiveCollection ? 'title-serif-4' : 'title-sans-4'}
        >
          {title}
        </Title>
        <Text className="mt-5 sm:text-md" text={description} theme="text-sans-1" />
      </section>
      {services.map(({ label, items }, idx) => (
        <section
          className={classnames('py-5', { 'border-b-1': idx !== services.length - 1 })}
          key={label}
        >
          <Title className="mb-3" hLevel={3} theme="title-sans-3">
            {label}
          </Title>
          <LabeledIconsList items={items} theme="list" color={color} />
        </section>
      ))}
    </Sheet>
  );
}
